import { useState, useRef, useEffect, useCallback } from "react";
import { Volume2, VolumeX, Music } from "lucide-react";

// Background music file
const MUSIC_URL = "https://customer-assets.emergentagent.com/job_goofy-hypatia-3/artifacts/z1z1mm8n_My-Light-Never-Dims-03-My-Dedication%20%281%29.mp3";

export const MusicPlayer = () => {
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showEnableButton, setShowEnableButton] = useState(true);

  const enableMusic = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      audio.volume = 0.5;
      await audio.play();
      setIsPlaying(true);
      setShowEnableButton(false);
    } catch (error) {
      console.debug("Cannot play audio:", error);
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Check if user previously muted
    const savedMuteState = localStorage.getItem("musicMuted");
    if (savedMuteState === "true") {
      setIsMuted(true);
      audio.muted = true;
    }

    audio.volume = 0.5;

    // Try to auto-play
    const tryAutoPlay = async () => {
      try {
        await audio.play();
        setIsPlaying(true);
        setShowEnableButton(false);
      } catch (error) {
        // Auto-play blocked - show enable button
        setShowEnableButton(true);
      }
    };

    tryAutoPlay();
  }, []); // Empty deps - only run on mount

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!isPlaying) {
      enableMusic();
      return;
    }

    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    audio.muted = newMutedState;
    localStorage.setItem("musicMuted", newMutedState.toString());
  };

  return (
    <>
      {/* Audio Element */}
      <audio
        ref={audioRef}
        src={MUSIC_URL}
        loop
        preload="auto"
        data-testid="background-music"
      />

      {/* Enable Music Floating Button - Shows when auto-play is blocked */}
      {showEnableButton && !isPlaying && (
        <button
          onClick={enableMusic}
          data-testid="music-enable-button"
          className="fixed bottom-24 right-6 z-50 flex items-center gap-2 bg-[#D4AF37] text-white px-4 py-3 rounded-full shadow-lg hover:bg-[#B8963A] transition-all animate-pulse hover:animate-none"
        >
          <Music className="w-5 h-5" />
          <span className="font-sans text-sm font-medium">Enable Music</span>
        </button>
      )}

      {/* Mute/Unmute Button - Shows when music is playing */}
      {isPlaying && (
        <button
          onClick={toggleMute}
          data-testid="music-toggle-button"
          className={`fixed bottom-24 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg border transition-all duration-300 group ${
            isMuted 
              ? "bg-white/90 border-gray-200 hover:bg-white" 
              : "bg-[#3A5A6A]/90 backdrop-blur-md border-white/20 hover:bg-[#3A5A6A]"
          }`}
          title={isMuted ? "Unmute music" : "Mute music"}
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
          ) : (
            <>
              <Volume2 className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
              <span className="absolute -top-1 -right-1 w-3 h-3">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#D4AF37]"></span>
              </span>
            </>
          )}
        </button>
      )}
    </>
  );
};

export default MusicPlayer;
